import { API_BASE_URL, getAuthHeaders } from '@/utils/apiConfig';
import { extractConnectionErrorMessage, type SchemaEntry } from '@/utils/connectionSettingsUtils';

export interface ConnectionDetails {
  sync_frequency: number;
  daily_sync_time: string;
  is_historical_sync: boolean;
  schedule_type: string;
  setup_state: string;
  service: string;
  sync_state?: string;
  rescheduled_for?: string | null;
  sync_tasks?: Array<{ message?: string; details?: string }>;
  sync_warnings?: Array<{ message?: string; details?: string }>;
  status_reason?: unknown;
  sync_start_date?: string;
  sync_start_date_origin?: 'csv' | 'manual';
  sync_start_date_set_at?: string;
}

export interface ConnectorDefaultSchema {
  id: string;
  connector_type: string;
  tables_to_sync: string[];
  updated_at: string;
  updated_by: string | null;
}

export interface ConnectionTableState {
  allowed_tables: string[];
  disabled_tables: string[];
}

export type SyncStartDateOrigin = 'csv' | 'manual';

const SETTINGS_FN = 'manage-connection-settings';
const DEFAULTS_FN = 'manage-connector-defaults';

const buildUrl = (fn: string, params?: Record<string, string | undefined>): string => {
  const query = Object.entries(params ?? {})
    .filter((entry): entry is [string, string] => entry[1] !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  return query ? `${API_BASE_URL}/${fn}?${query}` : `${API_BASE_URL}/${fn}`;
};

const send = async (url: string, body?: Record<string, unknown>): Promise<Response> => {
  const res = await fetch(url, {
    ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
    headers: await getAuthHeaders(),
  });
  if (!res.ok) throw new Error(extractConnectionErrorMessage(await res.text()));
  return res;
};

const get = async <T>(fn: string, params: Record<string, string | undefined>): Promise<T> =>
  (await send(buildUrl(fn, params))).json();

// Write actions don't all return a JSON body, so only read it where a caller needs it.
const post = async (fn: string, body: Record<string, unknown>): Promise<void> => {
  await send(buildUrl(fn), body);
};

export const getConnectionDetails = (connectorId: string, orgId?: string) =>
  get<ConnectionDetails>(SETTINGS_FN, { action: 'details', connectorId, orgId });

export const getConnectionSchemas = async (connectorId: string): Promise<Record<string, SchemaEntry>> => {
  const data = await get<{ data?: { schemas?: Record<string, SchemaEntry> }; schemas?: Record<string, SchemaEntry> }>(
    SETTINGS_FN,
    { action: 'schema', connectorId },
  );
  return data?.data?.schemas || data?.schemas || {};
};

/** Resolves to `null` only when the connection has no stored table state yet; failures reject. */
export const getConnectionTableState = async (
  connectorId: string,
  orgId: string,
): Promise<ConnectionTableState | null> => {
  const data = await get<{ allowed_tables?: unknown; disabled_tables?: string[] }>(
    SETTINGS_FN,
    { action: 'connection-table-state', connectorId, orgId },
  );
  return Array.isArray(data?.allowed_tables)
    ? { allowed_tables: data.allowed_tables as string[], disabled_tables: data.disabled_tables ?? [] }
    : null;
};

export const updateConnectionTableState = (
  connectorId: string,
  orgId: string,
  allowedTables: string[],
  disabledTables: string[],
) => post(SETTINGS_FN, { action: 'update-connection-table-state', connectorId, orgId, allowedTables, disabledTables });

export const updateSyncSettings = (connectorId: string, syncFrequency: number, dailySyncTime?: string) =>
  post(SETTINGS_FN, {
    action: 'update-settings',
    connectorId,
    sync_frequency: syncFrequency,
    ...(dailySyncTime !== undefined ? { daily_sync_time: dailySyncTime } : {}),
  });

export const updateConnectionSchema = (connectorId: string, schemas: Record<string, SchemaEntry>) =>
  post(SETTINGS_FN, { action: 'update-schema', connectorId, schemas });

export const getCsvLastDate = async (orgId: string): Promise<string | null> => {
  const res = await send(buildUrl(SETTINGS_FN), { action: 'get-csv-last-date', orgId });
  const data: { lastDate?: string | null } = await res.json();
  return data?.lastDate || null;
};

export const updateSyncStartDate = (
  connectorId: string,
  orgId: string,
  syncStartDate: string,
  origin: SyncStartDateOrigin,
) => post(SETTINGS_FN, { action: 'update-sync-start-date', connectorId, orgId, syncStartDate, origin });

export const resetSyncStartDate = (connectorId: string, orgId: string) =>
  post(SETTINGS_FN, { action: 'reset-sync-start-date', connectorId, orgId });

export const listConnectorDefaults = () => get<ConnectorDefaultSchema[]>(DEFAULTS_FN, { action: 'list' });

export const getConnectorDefaultTables = async (connectorType: string): Promise<string[]> => {
  const data = await get<{ tables_to_sync?: unknown }>(DEFAULTS_FN, { action: 'get', connectorType });
  return Array.isArray(data?.tables_to_sync) ? (data.tables_to_sync as string[]) : [];
};

export const updateConnectorDefaults = (connectorType: string, tablesToSync: string[]) =>
  post(DEFAULTS_FN, { action: 'update', connectorType, tablesToSync });

export const addConnectorType = (connectorType: string) =>
  post(DEFAULTS_FN, { action: 'add-connector', connectorType, tablesToSync: [] });
